import apiSlice from '../apiSlice';
import { GlobalFilterKeys } from '../types';
import { stripGlobalFilterKeys } from '../utils';

import type {
  MediaPlan,
  MediaPlanListItem,
  MediaPlansSearchParams,
} from '@/features/mediaPlan';
import type { MediaPlanFormValues } from '@/features/mediaPlan/schemas';
import type {
  Experiment,
  MediaPlanDetail,
  StrategyComparisonParams,
  StrategyComparisonResponse,
} from '@/features/mediaPlan/types';

type MediaPlanPayload = Omit<MediaPlanFormValues, 'audienceType'>;

import { PaginationResponse } from '@/shared/utils/types';

const MEDIA_PLANS_API_URL = '/api/media-plans';

const mediaPlansTags = apiSlice.enhanceEndpoints({
  addTagTypes: ['mediaPlans', 'experiment'],
});

const mediaPlansApiSlice = mediaPlansTags.injectEndpoints({
  endpoints: (builder) => ({
    getMediaPlans: builder.query<
      PaginationResponse<MediaPlanListItem>,
      GlobalFilterKeys & MediaPlansSearchParams
    >({
      query: (params) => ({
        url: `${MEDIA_PLANS_API_URL}/`,
        method: 'GET',
        params: stripGlobalFilterKeys(params),
      }),
      providesTags: [{ type: 'mediaPlans', id: 'LIST' }],
    }),

    getAllMediaPlans: builder.query<
      Pick<MediaPlan, 'id' | 'name'>[],
      GlobalFilterKeys
    >({
      query: () => ({
        url: `${MEDIA_PLANS_API_URL}/name-list/`,
        method: 'GET',
      }),
      providesTags: [{ type: 'mediaPlans', id: 'LIST' }],
    }),

    createMediaPlan: builder.mutation<MediaPlan, MediaPlanPayload>({
      query: (data) => ({
        url: `${MEDIA_PLANS_API_URL}/`,
        method: 'POST',
        data,
      }),
      invalidatesTags: [{ type: 'mediaPlans', id: 'LIST' }],
    }),

    updateMediaPlan: builder.mutation<
      MediaPlan,
      { id: number } & Partial<MediaPlanPayload>
    >({
      query: ({ id, ...data }) => ({
        url: `${MEDIA_PLANS_API_URL}/${String(id)}/`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'mediaPlans', id: 'LIST' },
        { type: 'mediaPlans', id },
      ],
    }),

    getMediaPlanDetail: builder.query<
      MediaPlanDetail,
      GlobalFilterKeys & { id: number }
    >({
      query: ({ id }) => ({
        url: `${MEDIA_PLANS_API_URL}/detail/${String(id)}/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'mediaPlans', id }],
    }),

    getMediaPlanStrategyComparison: builder.query<
      StrategyComparisonResponse,
      GlobalFilterKeys & StrategyComparisonParams
    >({
      query: ({ id, ...params }) => ({
        url: `${MEDIA_PLANS_API_URL}/${String(id)}/compare-strategy/`,
        method: 'GET',
        params: {
          ...stripGlobalFilterKeys(params),
        },
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'mediaPlans', id }],
    }),

    getExperiment: builder.query<Experiment, { id: number }>({
      query: ({ id }) => ({
        url: `${MEDIA_PLANS_API_URL}/${String(id)}/experiment/`,
        method: 'GET',
      }),
      providesTags: (_result, _error, { id }) => [{ type: 'experiment', id }],
    }),

    requestExperiment: builder.mutation<Experiment, { id: number }>({
      query: ({ id }) => ({
        url: `${MEDIA_PLANS_API_URL}/${String(id)}/experiment/request/`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'experiment', id },
      ],
    }),
  }),
});

export const {
  useGetMediaPlansQuery,
  useCreateMediaPlanMutation,
  useUpdateMediaPlanMutation,
  useGetAllMediaPlansQuery,
  useGetMediaPlanDetailQuery,
  useGetMediaPlanStrategyComparisonQuery,
  useGetExperimentQuery,
  useRequestExperimentMutation,
} = mediaPlansApiSlice;
